// Relative path works with Vite dev proxy (localhost) and nginx proxy (AWS).
// Override with FIVE_API_BASE_URL only when using a separate API gateway (e.g. localhost:8000).
const DEFAULT_BASE_URL =
  import.meta.env.FIVE_API_BASE_URL || '/api/user-profile';

const TOKEN_STORAGE_KEY = 'five_ai_access_token';
const REFRESH_TOKEN_STORAGE_KEY = 'five_ai_refresh_token';

/** Extract `exp` claim (ms) from JWT payload. Returns 0 on failure. */
function getTokenExpMs(token: string): number {
  try {
    const base64Url = token.split('.')[1] || '';
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const jsonPayload = atob(padded);
    const expSeconds = JSON.parse(jsonPayload).exp;
    return typeof expSeconds === 'number' ? expSeconds * 1000 : 0;
  } catch {
    return 0;
  }
}

export interface ApiError {
  message: string;
  code?: string;
  status?: number;
  details?: unknown;
}

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
  /** When true, do not retry on failure (for optional/non-critical requests). */
  skipRetry?: boolean;
}

class ApiService {
  private baseURL: string;
  private authToken: string | null;
  private defaultHeaders: HeadersInit;
  private refreshLock: Promise<boolean> | null = null;

  /** Set by AuthContext — attempts token refresh, returns new access token or null. */
  onUnauthorized: (() => Promise<string | null>) | null = null;
  /** Set by AuthContext — clears auth state and redirects to login. */
  onForceLogout: (() => void) | null = null;

  constructor(
    baseURL: string,
    options?: { defaultHeaders?: HeadersInit; initialAuthToken?: string | null }
  ) {
    this.baseURL = baseURL.replace(/\/$/, '');
    this.authToken = options?.initialAuthToken ?? null;
    this.defaultHeaders = options?.defaultHeaders || {
      'Content-Type': 'application/json',
    };
  }

  setAuthToken(token: string | null) {
    this.authToken = token;
    if (typeof window !== 'undefined') {
      if (token) {
        localStorage.setItem(TOKEN_STORAGE_KEY, token);
      } else {
        // IMPORTANT: Do NOT remove from localStorage when setting null.
        // On page refresh, AuthContext resets state to null before bootstrap runs.
        // If we remove from localStorage here, the buildHeaders fallback breaks.
        // Token should only be removed from localStorage via clearAuth() (explicit logout).
        // localStorage.removeItem(TOKEN_STORAGE_KEY); -- DISABLED: was causing refresh bug
      }
    }
  }

  getAuthToken() {
    return this.authToken;
  }

  /**
   * Coordinate token refresh across concurrent 401s.
   * Only one refresh call runs at a time; other callers await the same promise.
   */
  private async executeRefresh(): Promise<string | null> {
    if (this.refreshLock) {
      // Another refresh is already in-flight — wait for it, then return current token
      await this.refreshLock;
      return this.authToken;
    }
    let resolve: (v: boolean) => void;
    this.refreshLock = new Promise<boolean>((r) => {
      resolve = r;
    });
    try {
      const newToken = await this.onUnauthorized!();
      resolve!(!!newToken);
      return newToken;
    } catch {
      resolve!(false);
      return null;
    } finally {
      this.refreshLock = null;
    }
  }

  private buildHeaders(headers?: HeadersInit, skipAuth?: boolean) {
    // Note: X-Correlation-ID header removed to avoid CORS issues
    // Some backends don't allow custom headers in CORS preflight
    // If correlation ID is needed, it should be added as a query parameter
    // or the backend CORS configuration should be updated to allow this header
    
    const mergedHeaders = {
      ...this.defaultHeaders,
      ...headers,
      // Removed X-Correlation-ID to fix CORS preflight issues
      // The backend CORS configuration doesn't allow this header
    };

    // Only add Authorization header if skipAuth is false.
    // If AuthContext hasn't injected tokens yet (e.g. early page render on refresh),
    // fall back to localStorage so protected API calls still carry Authorization.
    // In local dev with skipAuth=true, no auth token is sent
    // Always try localStorage as fallback when authToken not set via context
    const fallbackToken =
      !skipAuth && !this.authToken && typeof window !== 'undefined'
        ? localStorage.getItem(TOKEN_STORAGE_KEY)
        : null;
    const effectiveToken = this.authToken ?? fallbackToken;

    if (!skipAuth && effectiveToken) {
      return {
        ...mergedHeaders,
        Authorization: `Bearer ${effectiveToken}`,
      };
    }

    return mergedHeaders;
  }

  private async handleResponse<T>(response: Response, endpoint?: string, method?: string): Promise<T> {
    // Handle 204 No Content (common for DELETE operations)
    if (response.status === 204) {
      return undefined as T;
    }
    
    const contentType = response.headers.get('content-type');
    const contentLength = response.headers.get('content-length');
    const isJson = contentType && contentType.includes('application/json');
    
    // Handle empty responses (e.g., 403 with no body)
    let payload: unknown;
    if (contentLength === '0' || (!contentType && response.status === 403)) {
      payload = { error: 'Forbidden', message: 'Access denied. Authentication may be required.' };
    } else if (isJson) {
      try {
        payload = await response.json();
      } catch (_e) {
        // If JSON parsing fails, try to get text
        payload = await response.text().catch(() => ({ error: 'Failed to parse response' }));
      }
    } else {
      payload = await response.text().catch(() => ({ error: 'Failed to read response' }));
    }

    // Handle 429 Rate Limit errors specifically - throw immediately with clear message
    if (response.status === 429) {
      const retryAfterHeader = response.headers.get('Retry-After');
      const retryAfterSeconds = retryAfterHeader ? parseInt(retryAfterHeader, 10) : null;
      
      console.error('[ApiService] Rate limit exceeded (429):', {
        endpoint,
        url: response.url,
        retryAfter: retryAfterHeader,
        retryAfterSeconds,
        payload,
      });
      
      // Extract message from payload if it's JSON
      let errorMessage = 'Rate limit exceeded. Please wait a moment and try again.';
      if (isJson && typeof payload === 'object' && payload !== null && 'message' in payload) {
        errorMessage = (payload as { message: string }).message || errorMessage;
      }
      
      // Enhance error message with retry timing if available
      if (retryAfterSeconds && retryAfterSeconds > 0) {
        const retryTime = retryAfterSeconds < 60 
          ? `${retryAfterSeconds} second${retryAfterSeconds !== 1 ? 's' : ''}`
          : `${Math.ceil(retryAfterSeconds / 60)} minute${Math.ceil(retryAfterSeconds / 60) !== 1 ? 's' : ''}`;
        errorMessage += ` Please try again in ${retryTime}.`;
      }
      
      const error: ApiError = {
        message: errorMessage,
        status: 429,
        details: {
          ...(typeof payload === 'object' && payload !== null ? payload : { originalPayload: payload }),
          retryAfter: retryAfterSeconds,
          retryAfterHeader,
        },
      };
      throw error;
    }

    if (!response.ok) {
      // Only return empty paginated response for GET requests to list endpoints (not POST/PUT/PATCH/DELETE)
      // Write operations (POST/PUT/PATCH/DELETE) should always throw errors so callers can handle them properly
      const isGetRequest = !method || method.toUpperCase() === 'GET';

      // Always throw on 401 — auth errors must surface so the UI can redirect to login
      // and so React Query can mark the query as failed (not silently return empty data).
      if (response.status === 401) {
        const error: ApiError = {
          message: 'Authentication required. Please log in to continue.',
          status: 401,
          details: payload,
        };
        throw error;
      }

      // For GET list endpoints with any error status (404, 500, etc.), return empty paginated response instead of throwing
      // This handles cases where the database is empty or the service is unavailable
      if (isGetRequest && endpoint) {
        // Check if it's a list endpoint (no ID pattern at the end)
        const urlPath = endpoint.split('?')[0]; // Remove query params
        
        // Check for UUID pattern (8-4-4-4-12 hex chars with dashes): /resource/123e4567-e89b-12d3-a456-426614174000
        const uuidPattern = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        // Check for MongoDB ObjectId pattern (24 hex chars): /api/v1/permissions/69560a803f44966090a5122f
        const mongoIdPattern = /\/[0-9a-f]{24}$/i;
        // Check for generic detail endpoint (ID or email at the end, but not just /profiles)
        const genericDetailPattern = /\/profiles\/[^/]+$/i;
        
        // Detail endpoints: /api/v1/permissions/{24-hex-id}, /api/v1/roles/{uuid}, /api/v1/profiles/username/{username}, /api/v1/profiles/email/{email}
        const isDetailEndpoint = 
          uuidPattern.test(urlPath) || 
          mongoIdPattern.test(urlPath) || 
          genericDetailPattern.test(urlPath) ||
          urlPath.includes('/username/') || 
          urlPath.includes('/email/');
        
        // Only return empty response for GET list endpoints (not detail endpoints, not POST/PUT/PATCH/DELETE)
        // Detail endpoints and write operations should throw errors so the UI can handle them properly
        if (!isDetailEndpoint) {
          // Return empty paginated response structure for GET list endpoint errors
          // This allows the UI to show "No data found" instead of "Error loading data"
          return {
            content: [],
            page: {
              size: 0,
              number: 0,
              totalElements: 0,
              totalPages: 0,
            },
          } as T;
        }
      }
      
      // Extract error message from payload
      let errorMessage = 'Unexpected error';
      
      // Special handling for 403 Forbidden - provide clear message
      if (response.status === 403) {
        errorMessage = 'Access forbidden. This endpoint may require authentication or the session may not have permission to submit responses.';
      }
      
      if (isJson && payload && typeof payload === 'object') {
        // Log the full error payload for debugging (especially for 400/403 errors)
        if (response.status === 400 || response.status === 403) {
          console.error(`[ApiService] ${response.status} Error - Full error payload:`, JSON.stringify(payload, null, 2));
        }
        
        // Handle standard error response format with message field
        if ('message' in payload && typeof payload.message === 'string' && payload.message) {
          errorMessage = payload.message;
        } 
        // Handle error field if message is not available
        else if ('error' in payload && typeof payload.error === 'string' && payload.error) {
          errorMessage = payload.error;
        }
        // Handle validation errors array if present
        else if ('validationErrors' in payload && Array.isArray(payload.validationErrors) && payload.validationErrors.length > 0) {
          const firstError = payload.validationErrors[0];
          if (typeof firstError === 'string') {
            errorMessage = firstError;
          } else if (firstError && typeof firstError === 'object' && 'message' in firstError) {
            errorMessage = String(firstError.message);
          }
        }
        // Try to extract any other error information
        else {
          // Log all keys to help debug
          console.warn('[ApiService] Error payload keys:', Object.keys(payload));
          // Try to find any string value that might be an error message
          for (const [key, value] of Object.entries(payload)) {
            if (typeof value === 'string' && value.length > 0 && (key.toLowerCase().includes('error') || key.toLowerCase().includes('message'))) {
              errorMessage = value;
              break;
            }
          }
        }
      }
      
      // Fallback to status text if no message found
      if (errorMessage === 'Unexpected error' && response.statusText) {
        errorMessage = response.statusText;
      }
      
      const error: ApiError = {
        message: errorMessage,
        status: response.status,
        details: payload,
      };
      throw error;
    }

    return payload as T;
  }

  /**
   * Retry logic with exponential backoff
   * Retries on network errors or 5xx server errors
   */
  private async retryRequest(
    url: string,
    config: RequestInit,
    retries = 3,
    delay = 2000 // Increased initial delay to 2 seconds
  ): Promise<Response> {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        // Ensure mode is 'cors' for cross-origin requests
        // When skipAuth is true, we don't send any auth tokens, so don't send credentials either
        const finalConfig: RequestInit = {
          ...config,
          mode: 'cors',
        };
        // For skipAuth requests, explicitly omit credentials to match backend expectations
        // The backend allows credentials, but since we're not sending auth, we shouldn't send credentials
        if (!('credentials' in finalConfig) && config.method !== 'GET') {
          // For non-GET requests with skipAuth, explicitly omit credentials
          finalConfig.credentials = 'omit';
        }
        const response = await fetch(url, finalConfig);
        
        // Handle successful responses first
        if (response.ok) {
          return response;
        }
        
        // Handle 429 rate limit - don't retry, return immediately to prevent cascading failures
        if (response.status === 429) {
          // For 429 errors, don't retry - return immediately to prevent rate limit cascade
          // The error handler will show a user-friendly message
          console.error(`Rate limit exceeded (429). Stopping retries to prevent cascading failures.`);
          return response;
        }
        
        // On 401, attempt transparent token refresh and retry once
        if (response.status === 401 && this.onUnauthorized) {
          const newToken = await this.executeRefresh();
          if (newToken) {
            // Retry the original request with the new token
            const retryHeaders = { ...config.headers } as Record<string, string>;
            retryHeaders['Authorization'] = `Bearer ${newToken}`;
            return fetch(url, { ...config, headers: retryHeaders });
          }

          // Refresh failed.
          // If access token still looks unexpired (token-only session), avoid forcing logout,
          // because some services (e.g. profile-service) may reject tokens with different
          // audiences/scopes even though learning endpoints are still usable.
          try {
            const storedToken =
              this.authToken ??
              (typeof window !== 'undefined' ? localStorage.getItem(TOKEN_STORAGE_KEY) : null);
            const expMs = storedToken ? getTokenExpMs(storedToken) : 0;
            const now = Date.now();
            if (expMs === 0 || expMs > now) {
              return response;
            }
          } catch {
            // If token decoding fails, fall through to force logout.
          }

          // Access token expired or unparseable — force logout
          try {
            const storedToken =
              this.authToken ??
              (typeof window !== 'undefined' ? localStorage.getItem(TOKEN_STORAGE_KEY) : null);
            const expMs = storedToken ? getTokenExpMs(storedToken) : 0;
            const now = Date.now();
            console.warn('[ApiService] onForceLogout() due to 401; access token looks expired/unparseable', {
              url: response.url,
              expMs,
              now,
              refreshTokenPresent: typeof window !== 'undefined' ? !!localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY) : null,
            });
          } catch {
            // ignore debug logging failures
          }
          this.onForceLogout?.();
          return response;
        }

        // Don't retry on other 4xx errors (client errors)
        if (response.status >= 400 && response.status < 500) {
          return response;
        }
        
        // Retry on 5xx server errors with longer delays
        if (response.status >= 500 && attempt < retries) {
          // Exponential backoff: 2s, 4s, 8s (increased delays)
          const backoffDelay = delay * Math.pow(2, attempt);
          console.warn(`Server error (${response.status}), retrying in ${backoffDelay / 1000}s... (attempt ${attempt + 1}/${retries + 1})`);
          await new Promise((resolve) => setTimeout(resolve, backoffDelay));
          continue;
        }
        
        // Return response for any other status codes
        return response;
      } catch (error) {
        // Network error - retry if attempts remaining with longer delays
        if (attempt < retries) {
          const backoffDelay = delay * Math.pow(2, attempt);
          console.warn(`Network error, retrying in ${backoffDelay / 1000}s... (attempt ${attempt + 1}/${retries + 1})`);
          await new Promise((resolve) => setTimeout(resolve, backoffDelay));
          continue;
        }
        throw error;
      }
    }
    
    // Should never reach here, but TypeScript needs it
    throw new Error('Request failed after all retries');
  }

  async request<T>(endpoint: string, options: RequestOptions = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const { skipAuth, skipRetry, headers, ...rest } = options;
    let requestHeaders = this.buildHeaders(headers, skipAuth);
    // When body is FormData, do not set Content-Type so the browser sets multipart/form-data with boundary
    if (rest.body instanceof FormData) {
      requestHeaders = { ...requestHeaders } as Record<string, string>;
      delete (requestHeaders as Record<string, string>)['Content-Type'];
    }
    const config: RequestInit = {
      ...rest,
      headers: requestHeaders,
      // Don't set credentials explicitly - let browser handle it
      // Only set if explicitly needed
    };

    // Use retry logic for GET requests (read operations)
    // For write operations (POST, PUT, DELETE), allow retries for 429 rate limit errors and network errors
    // skipRetry: no retries (for optional/non-critical calls like location update)
    const isReadOperation = !rest.method || rest.method === 'GET';
    const maxRetries = skipRetry ? 0 : isReadOperation ? 3 : 2;
    
    // Debug logging (only in development)
    if (import.meta.env.DEV) {
      console.log(`[ApiService] ${rest.method || 'GET'} ${url}`);
    }

    try {
      const response = await this.retryRequest(url, config, maxRetries);
      return this.handleResponse<T>(response, endpoint, config.method);
    } catch (error) {
      console.error(`[ApiService] Request failed:`, {
        url,
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  }

  get<T>(endpoint: string, options?: RequestOptions) {
    return this.request<T>(endpoint, { method: 'GET', ...options });
  }

  post<T>(endpoint: string, data?: unknown, options?: RequestOptions) {
    const body = data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined);
    return this.request<T>(endpoint, {
      method: 'POST',
      body,
      ...options,
    });
  }

  put<T>(endpoint: string, data?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  patch<T>(endpoint: string, data?: unknown, options?: RequestOptions) {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  delete<T>(endpoint: string, options?: RequestOptions) {
    return this.request<T>(endpoint, { method: 'DELETE', ...options });
  }
}

export const createApiService = (baseURL: string) =>
  new ApiService(baseURL);

export const apiService = createApiService(DEFAULT_BASE_URL);

export const userApi = {
  getProfile: () => apiService.get('/api/v1/user/profile'),
  updateProfile: (data: unknown) => apiService.put('/api/v1/user/profile', data),
};
